import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Card, Badge, ListGroup, Accordion, Alert, Spinner } from 'react-bootstrap';
import { FaCodeBranch, FaTimes, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { MdAdd, MdRemove, MdEdit } from 'react-icons/md';
import ReactFlow, { Background, BackgroundVariant, Controls } from 'reactflow';
import 'reactflow/dist/style.css';
import ReactDiffViewer from 'react-diff-viewer-continued';
import StepNode from '../workflow/nodes/StepNode';
import GroupNode from '../workflow/nodes/GroupNode';

const nodeTypes = {
  stepNode: StepNode,
  groupNode: GroupNode,
};

function RunbookVersionCompare({ show, onHide, version1, version2 }) {
  const [loading, setLoading] = useState(true);
  const [comparison, setComparison] = useState(null);
  const [viewMode, setViewMode] = useState('visual'); // 'visual', 'detailed', or 'json'
  const [expandedSections, setExpandedSections] = useState({});
  const [showDiffOnly, setShowDiffOnly] = useState(true);

  useEffect(() => {
    if (show && version1 && version2) {
      performComparison();
    }
  }, [show, version1, version2]);

  const performComparison = () => {
    setLoading(true);
    try {
      // Parse workflow JSON from both versions
      const workflow1 = JSON.parse(version1.runbookString || '{}');
      const workflow2 = JSON.parse(version2.runbookString || '{}');

      // Perform comparison
      const changes = compareWorkflows(workflow1, workflow2);
      setComparison(changes);
    } catch (error) {
      setComparison({ error: `Failed to compare versions: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const compareWorkflows = (workflow1, workflow2) => {
    // Build edges from steps' nextStepId
    const edges1 = buildEdgesFromSteps(workflow1.steps || []);
    const edges2 = buildEdgesFromSteps(workflow2.steps || []);

    const changes = {
      metadata: compareMetadata(workflow1, workflow2),
      steps: compareSteps(workflow1.steps || [], workflow2.steps || []),
      edges: compareEdges(edges1, edges2),
      connectors: compareArrays(workflow1.connectors || [], workflow2.connectors || [], 'connectorId'),
      variables: compareArrays(workflow1.runbookVariables || [], workflow2.runbookVariables || [], 'variableNumber'),
      stats: {
        stepsAdded: 0,
        stepsRemoved: 0,
        stepsModified: 0,
        edgesAdded: 0,
        edgesRemoved: 0,
      }
    };

    // Calculate stats
    changes.stats.stepsAdded = changes.steps.added.length;
    changes.stats.stepsRemoved = changes.steps.removed.length;
    changes.stats.stepsModified = changes.steps.modified.length;
    changes.stats.edgesAdded = changes.edges.added.length;
    changes.stats.edgesRemoved = changes.edges.removed.length;

    return changes;
  };

  const buildEdgesFromSteps = (steps) => {
    const edges = [];
    steps.forEach(step => {
      // Handle regular nextStepId
      if (step.nextStepId) {
        edges.push({
          id: `${step.stepNumber}-${step.nextStepId}`,
          source: step.stepNumber,
          target: step.nextStepId
        });
      }

      // Handle if/else conditional paths
      if (step.conditionTruePath) {
        edges.push({
          id: `${step.stepNumber}-${step.conditionTruePath}-true`,
          source: step.stepNumber,
          target: step.conditionTruePath,
          label: 'true',
          style: { stroke: '#28a745' }
        });
      }

      if (step.conditionFalsePath) {
        edges.push({
          id: `${step.stepNumber}-${step.conditionFalsePath}-false`,
          source: step.stepNumber,
          target: step.conditionFalsePath,
          label: 'false',
          style: { stroke: '#dc3545' }
        });
      }
    });
    return edges;
  };

  const compareMetadata = (workflow1, workflow2) => {
    const changes = [];

    // Compare basic properties
    const properties = ['runbookName', 'runbookDescription', 'isAsync', 'sendEmail'];
    properties.forEach(prop => {
      if (workflow1[prop] !== workflow2[prop]) {
        changes.push({
          property: prop,
          oldValue: workflow1[prop],
          newValue: workflow2[prop]
        });
      }
    });

    return changes;
  };

  const compareSteps = (steps1, steps2) => {
    const added = [];
    const removed = [];
    const modified = [];

    const steps1Map = new Map(steps1.map(s => [s.stepNumber, s]));
    const steps2Map = new Map(steps2.map(s => [s.stepNumber, s]));

    // Find added and modified steps
    steps2.forEach(step2 => {
      const step1 = steps1Map.get(step2.stepNumber);
      if (!step1) {
        added.push(step2);
      } else {
        const differences = compareStepData(step1, step2);
        if (differences.length > 0) {
          modified.push({
            id: step2.stepNumber,
            label: step2.stepName,
            stepType: getStepTypeName(step2.action),
            differences
          });
        }
      }
    });

    // Find removed steps
    steps1.forEach(step1 => {
      if (!steps2Map.has(step1.stepNumber)) {
        removed.push(step1);
      }
    });

    return { added, removed, modified };
  };

  const compareStepData = (step1, step2) => {
    const differences = [];

    // Properties to compare (excluding position changes as they're usually just cosmetic)
    const properties = [
      'stepName', 'stepDescription', 'action', 'connectorId', 'system', 'systemObject',
      'fieldsToQuery', 'filter', 'condition', 'customErrorMessage', 'netChange',
      'dataStep', 'nextStepId', 'parentStepId', 'groupId',
      'inspectRequest', 'serialProcessing', 'errorUponCompletion',
      'conditionTruePath', 'conditionFalsePath',
      'debugMode', 'debugStartingAt', 'debugNumberToProcess',
      'setVarType', 'connectorNumber', 'stepOrder'
    ];

    properties.forEach(prop => {
      if (JSON.stringify(step1[prop]) !== JSON.stringify(step2[prop])) {
        differences.push({
          property: prop,
          oldValue: step1[prop],
          newValue: step2[prop]
        });
      }
    });

    // Compare field mappings separately
    const fieldMappings1 = step1.fieldMappings || [];
    const fieldMappings2 = step2.fieldMappings || [];
    if (JSON.stringify(fieldMappings1) !== JSON.stringify(fieldMappings2)) {
      differences.push({
        property: 'fieldMappings',
        oldValue: fieldMappings1,
        newValue: fieldMappings2,
        isComplex: true
      });
    }

    // Note: Position changes are ignored for modification detection since they're usually just cosmetic

    return differences;
  };

  const compareEdges = (edges1, edges2) => {
    const added = [];
    const removed = [];

    const edges1Set = new Set(edges1.map(e => `${e.source}-${e.target}`));
    const edges2Set = new Set(edges2.map(e => `${e.source}-${e.target}`));

    edges2.forEach(edge2 => {
      const key = `${edge2.source}-${edge2.target}`;
      if (!edges1Set.has(key)) {
        added.push(edge2);
      }
    });

    edges1.forEach(edge1 => {
      const key = `${edge1.source}-${edge1.target}`;
      if (!edges2Set.has(key)) {
        removed.push(edge1);
      }
    });

    return { added, removed };
  };

  const compareArrays = (arr1, arr2, idKey) => {
    const added = [];
    const removed = [];
    const modified = [];

    const arr1Map = new Map(arr1.map(item => [item[idKey], item]));
    const arr2Map = new Map(arr2.map(item => [item[idKey], item]));

    arr2.forEach(item2 => {
      const item1 = arr1Map.get(item2[idKey]);
      if (!item1) {
        added.push(item2);
      } else if (JSON.stringify(item1) !== JSON.stringify(item2)) {
        modified.push({
          id: item2[idKey],
          before: item1,
          after: item2
        });
      }
    });

    arr1.forEach(item1 => {
      if (!arr2Map.has(item1[idKey])) {
        removed.push(item1);
      }
    });

    return { added, removed, modified };
  };

  const getNodeStyle = (changeType) => {
    const baseStyle = {
      border: '2px solid',
      borderRadius: '8px',
    };

    switch (changeType) {
      case 'added':
        return { ...baseStyle, borderColor: '#28a745', backgroundColor: '#d4edda' };
      case 'removed':
        return { ...baseStyle, borderColor: '#dc3545', backgroundColor: '#f8d7da' };
      case 'modified':
        return { ...baseStyle, borderColor: '#ffc107', backgroundColor: '#fff3cd' };
      default:
        return baseStyle;
    }
  };

  const prepareNodesForDisplay = (version, workflow, changeType) => {
    const steps = workflow.steps || [];

    // Convert steps to ReactFlow nodes format
    const nodes = steps.map(step => ({
      id: step.stepNumber,
      type: 'stepNode',
      position: { x: step.positionX || 0, y: step.positionY || 0 },
      data: {
        label: step.stepName || step.stepNumber,
        stepType: getStepTypeName(step.action),
        description: step.stepDescription,
        action: step.action,
        stepId: step.stepNumber,
        ...step,
        readOnly: true
      },
      style: getNodeStyle(changeType)
    }));

    return nodes;
  };

  const getStepTypeName = (action) => {
    const actionMap = {
      1: 'query',
      2: 'create',
      3: 'update',
      4: 'delete',
      5: 'forEach',
      6: 'endLoop',
      7: 'setVar',
      8: 'declareVar',
      9: 'logMessage',
      10: 'lookup',
      11: 'ifElse',
      12: 'sendEmail',
      13: 'upsertClientVar',
      14: 'queryTableCache',
      15: 'lookupTableCache',
      20: 'buildTableCache',
      // Add more as needed
    };
    return actionMap[action] || 'unknown';
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const renderChangeIcon = (type) => {
    switch (type) {
      case 'added':
        return <MdAdd className="text-success" />;
      case 'removed':
        return <MdRemove className="text-danger" />;
      case 'modified':
        return <MdEdit className="text-warning" />;
      default:
        return null;
    }
  };

  const renderValue = (value) => {
    if (value === null || value === undefined) return <span className="text-muted">null</span>;
    if (typeof value === 'boolean') return value.toString();
    if (typeof value === 'object') return <pre className="mb-0">{JSON.stringify(value, null, 2)}</pre>;
    return value.toString();
  };

  const getStepLabel = (stepNumber, workflow) => {
    const step = workflow.steps?.find(s => s.stepNumber === stepNumber);
    if (step && step.stepName) {
      return `${stepNumber} (${step.stepName})`;
    }
    return stepNumber;
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={onHide} size="xl" fullscreen>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaCodeBranch className="me-2" />
          Compare Versions: v{version1?.versionNum} → v{version2?.versionNum}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-3">Analyzing changes...</p>
          </div>
        ) : comparison?.error ? (
          <Alert variant="danger">{comparison.error}</Alert>
        ) : (
          <>
            {/* Summary Stats */}
            <Row className="mb-4">
              <Col>
                <Card>
                  <Card.Body>
                    <h6 className="mb-3">Change Summary</h6>
                    <div className="d-flex gap-4 flex-wrap">
                      <div>
                        <Badge bg="success" className="me-2">
                          <MdAdd /> {comparison.stats.stepsAdded}
                        </Badge>
                        Steps Added
                      </div>
                      <div>
                        <Badge bg="danger" className="me-2">
                          <MdRemove /> {comparison.stats.stepsRemoved}
                        </Badge>
                        Steps Removed
                      </div>
                      <div>
                        <Badge bg="warning" className="me-2">
                          <MdEdit /> {comparison.stats.stepsModified}
                        </Badge>
                        Steps Modified
                      </div>
                      <div>
                        <Badge bg="success" className="me-2">
                          {comparison.stats.edgesAdded}
                        </Badge>
                        Connections Added
                      </div>
                      <div>
                        <Badge bg="danger" className="me-2">
                          {comparison.stats.edgesRemoved}
                        </Badge>
                        Connections Removed
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* View Mode Toggle */}
            <div className="mb-3">
              <Button
                variant={viewMode === 'visual' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => setViewMode('visual')}
                className="me-2"
              >
                Visual Diff
              </Button>
              <Button
                variant={viewMode === 'detailed' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => setViewMode('detailed')}
                className="me-2"
              >
                Detailed Changes
              </Button>
              <Button
                variant={viewMode === 'json' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => setViewMode('json')}
              >
                JSON Diff
              </Button>
            </div>

            {/* Visual Diff View */}
            {viewMode === 'visual' && (() => {
              const workflow1 = JSON.parse(version1.runbookString || '{}');
              const workflow2 = JSON.parse(version2.runbookString || '{}');
              const edges1 = buildEdgesFromSteps(workflow1.steps || []);
              const edges2 = buildEdgesFromSteps(workflow2.steps || []);

              return (
                <Row>
                  <Col md={6}>
                    <Card className="mb-3">
                      <Card.Header className="bg-danger text-white">
                        <h6 className="mb-0">Version {version1.versionNum} (Before)</h6>
                        <small>{version1.description || 'No description'}</small>
                      </Card.Header>
                      <Card.Body style={{ height: '600px', padding: 0 }}>
                        <ReactFlow
                          nodes={prepareNodesForDisplay(version1, workflow1, 'removed')}
                          edges={edges1}
                          nodeTypes={nodeTypes}
                          fitView
                          attributionPosition="bottom-left"
                          nodesDraggable={false}
                          nodesConnectable={false}
                          elementsSelectable={false}
                        >
                          <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
                          <Controls showInteractive={false} />
                        </ReactFlow>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="mb-3">
                      <Card.Header className="bg-success text-white">
                        <h6 className="mb-0">Version {version2.versionNum} (After)</h6>
                        <small>{version2.description || 'No description'}</small>
                      </Card.Header>
                      <Card.Body style={{ height: '600px', padding: 0 }}>
                        <ReactFlow
                          nodes={prepareNodesForDisplay(version2, workflow2, 'added')}
                          edges={edges2}
                          nodeTypes={nodeTypes}
                          fitView
                          attributionPosition="bottom-left"
                          nodesDraggable={false}
                          nodesConnectable={false}
                          elementsSelectable={false}
                        >
                          <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
                          <Controls showInteractive={false} />
                        </ReactFlow>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              );
            })()}

            {/* Detailed Changes View */}
            {viewMode === 'detailed' && (
              <Accordion defaultActiveKey="0">
                {/* Metadata Changes */}
                {comparison.metadata.length > 0 && (
                  <Accordion.Item eventKey="metadata">
                    <Accordion.Header>
                      <MdEdit className="me-2 text-warning" />
                      Metadata Changes ({comparison.metadata.length})
                    </Accordion.Header>
                    <Accordion.Body>
                      <ListGroup>
                        {comparison.metadata.map((change, idx) => (
                          <ListGroup.Item key={idx}>
                            <strong>{change.property}:</strong>
                            <Row className="mt-2">
                              <Col md={6}>
                                <div className="text-danger">
                                  <small>Before:</small>
                                  <div>{renderValue(change.oldValue)}</div>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="text-success">
                                  <small>After:</small>
                                  <div>{renderValue(change.newValue)}</div>
                                </div>
                              </Col>
                            </Row>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </Accordion.Body>
                  </Accordion.Item>
                )}

                {/* Added Steps */}
                {comparison.steps.added.length > 0 && (
                  <Accordion.Item eventKey="steps-added">
                    <Accordion.Header>
                      <MdAdd className="me-2 text-success" />
                      Steps Added ({comparison.steps.added.length})
                    </Accordion.Header>
                    <Accordion.Body>
                      <ListGroup>
                        {comparison.steps.added.map((step, idx) => (
                          <ListGroup.Item key={idx} className="border-success">
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <strong>{step.stepNumber}</strong>
                                {step.stepName && <span className="ms-2">({step.stepName})</span>}
                                <Badge bg="secondary" className="ms-2">{getStepTypeName(step.action)}</Badge>
                              </div>
                              <Badge bg="success">Added</Badge>
                            </div>
                            {step.stepDescription && (
                              <div className="text-muted mt-2">
                                <small>{step.stepDescription}</small>
                              </div>
                            )}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </Accordion.Body>
                  </Accordion.Item>
                )}

                {/* Removed Steps */}
                {comparison.steps.removed.length > 0 && (
                  <Accordion.Item eventKey="steps-removed">
                    <Accordion.Header>
                      <MdRemove className="me-2 text-danger" />
                      Steps Removed ({comparison.steps.removed.length})
                    </Accordion.Header>
                    <Accordion.Body>
                      <ListGroup>
                        {comparison.steps.removed.map((step, idx) => (
                          <ListGroup.Item key={idx} className="border-danger">
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <strong>{step.stepNumber}</strong>
                                {step.stepName && <span className="ms-2">({step.stepName})</span>}
                                <Badge bg="secondary" className="ms-2">{getStepTypeName(step.action)}</Badge>
                              </div>
                              <Badge bg="danger">Removed</Badge>
                            </div>
                            {step.stepDescription && (
                              <div className="text-muted mt-2">
                                <small>{step.stepDescription}</small>
                              </div>
                            )}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </Accordion.Body>
                  </Accordion.Item>
                )}

                {/* Modified Steps */}
                {comparison.steps.modified.length > 0 && (
                  <Accordion.Item eventKey="steps-modified">
                    <Accordion.Header>
                      <MdEdit className="me-2 text-warning" />
                      Steps Modified ({comparison.steps.modified.length})
                    </Accordion.Header>
                    <Accordion.Body>
                      {comparison.steps.modified.map((step, idx) => (
                        <Card key={idx} className="mb-3 border-warning">
                          <Card.Header className="bg-warning bg-opacity-10">
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <strong>{step.id}</strong>
                                {step.label && <span className="ms-2">({step.label})</span>}
                                <Badge bg="secondary" className="ms-2">{step.stepType}</Badge>
                              </div>
                              <Badge bg="warning">{step.differences.length} changes</Badge>
                            </div>
                          </Card.Header>
                          <Card.Body>
                            <ListGroup variant="flush">
                              {step.differences.map((diff, diffIdx) => (
                                <ListGroup.Item key={diffIdx}>
                                  <strong>{diff.property}:</strong>
                                  {diff.isPosition ? (
                                    <div className="mt-2 small">
                                      <span className="text-muted">Position changed</span>
                                    </div>
                                  ) : diff.isComplex ? (
                                    <div className="mt-2">
                                      <Row>
                                        <Col md={6}>
                                          <div className="text-danger">
                                            <small>Before ({diff.oldValue.length} mappings):</small>
                                            <pre className="small">{JSON.stringify(diff.oldValue, null, 2)}</pre>
                                          </div>
                                        </Col>
                                        <Col md={6}>
                                          <div className="text-success">
                                            <small>After ({diff.newValue.length} mappings):</small>
                                            <pre className="small">{JSON.stringify(diff.newValue, null, 2)}</pre>
                                          </div>
                                        </Col>
                                      </Row>
                                    </div>
                                  ) : (
                                    <Row className="mt-2">
                                      <Col md={6}>
                                        <div className="text-danger">
                                          <small>Before:</small>
                                          <div>{renderValue(diff.oldValue)}</div>
                                        </div>
                                      </Col>
                                      <Col md={6}>
                                        <div className="text-success">
                                          <small>After:</small>
                                          <div>{renderValue(diff.newValue)}</div>
                                        </div>
                                      </Col>
                                    </Row>
                                  )}
                                </ListGroup.Item>
                              ))}
                            </ListGroup>
                          </Card.Body>
                        </Card>
                      ))}
                    </Accordion.Body>
                  </Accordion.Item>
                )}

                {/* Connection Changes (Edges) */}
                {(comparison.edges.added.length > 0 ||
                  comparison.edges.removed.length > 0) && (() => {
                    const workflow2 = JSON.parse(version2.runbookString || '{}');
                    const workflow1 = JSON.parse(version1.runbookString || '{}');
                    return (
                      <Accordion.Item eventKey="connections">
                        <Accordion.Header>
                          Connection Changes
                        </Accordion.Header>
                        <Accordion.Body>
                          {comparison.edges.added.length > 0 && (
                            <div className="mb-3">
                              <h6 className="text-success"><MdAdd /> Added ({comparison.edges.added.length})</h6>
                              <ListGroup>
                                {comparison.edges.added.map((edge, idx) => (
                                  <ListGroup.Item key={idx} className="border-success">
                                    <strong>{getStepLabel(edge.source, workflow2)}</strong> → <strong>{getStepLabel(edge.target, workflow2)}</strong>
                                    {edge.label && <Badge bg="secondary" className="ms-2">{edge.label}</Badge>}
                                  </ListGroup.Item>
                                ))}
                              </ListGroup>
                            </div>
                          )}
                          {comparison.edges.removed.length > 0 && (
                            <div className="mb-3">
                              <h6 className="text-danger"><MdRemove /> Removed ({comparison.edges.removed.length})</h6>
                              <ListGroup>
                                {comparison.edges.removed.map((edge, idx) => (
                                  <ListGroup.Item key={idx} className="border-danger">
                                    <strong>{getStepLabel(edge.source, workflow1)}</strong> → <strong>{getStepLabel(edge.target, workflow1)}</strong>
                                    {edge.label && <Badge bg="secondary" className="ms-2">{edge.label}</Badge>}
                                  </ListGroup.Item>
                                ))}
                              </ListGroup>
                            </div>
                          )}
                        </Accordion.Body>
                      </Accordion.Item>
                    );
                  })()}

                {/* Connector Changes */}
                {(comparison.connectors.added.length > 0 ||
                  comparison.connectors.removed.length > 0 ||
                  comparison.connectors.modified.length > 0) && (
                  <Accordion.Item eventKey="connectors">
                    <Accordion.Header>
                      Connector Changes
                    </Accordion.Header>
                    <Accordion.Body>
                      {comparison.connectors.added.length > 0 && (
                        <div className="mb-3">
                          <h6 className="text-success"><MdAdd /> Added ({comparison.connectors.added.length})</h6>
                          <ListGroup>
                            {comparison.connectors.added.map((conn, idx) => (
                              <ListGroup.Item key={idx}>
                                {conn.connectorNumber} - {conn.system} - {conn.description}
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        </div>
                      )}
                      {comparison.connectors.removed.length > 0 && (
                        <div className="mb-3">
                          <h6 className="text-danger"><MdRemove /> Removed ({comparison.connectors.removed.length})</h6>
                          <ListGroup>
                            {comparison.connectors.removed.map((conn, idx) => (
                              <ListGroup.Item key={idx}>
                                {conn.connectorNumber} - {conn.system} - {conn.description}
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        </div>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                )}

                {/* Variable Changes */}
                {(comparison.variables.added.length > 0 ||
                  comparison.variables.removed.length > 0 ||
                  comparison.variables.modified.length > 0) && (
                  <Accordion.Item eventKey="variables">
                    <Accordion.Header>
                      Variable Changes
                    </Accordion.Header>
                    <Accordion.Body>
                      {comparison.variables.added.length > 0 && (
                        <div className="mb-3">
                          <h6 className="text-success"><MdAdd /> Added ({comparison.variables.added.length})</h6>
                          <ListGroup>
                            {comparison.variables.added.map((v, idx) => (
                              <ListGroup.Item key={idx}>
                                {v.variableNumber} - {v.variableName}
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        </div>
                      )}
                      {comparison.variables.removed.length > 0 && (
                        <div className="mb-3">
                          <h6 className="text-danger"><MdRemove /> Removed ({comparison.variables.removed.length})</h6>
                          <ListGroup>
                            {comparison.variables.removed.map((v, idx) => (
                              <ListGroup.Item key={idx}>
                                {v.variableNumber} - {v.variableName}
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        </div>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                )}
              </Accordion>
            )}

            {/* JSON Diff View */}
            {viewMode === 'json' && (
              <>
                <div className="mb-2 d-flex justify-content-between align-items-center">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="showDiffOnly"
                      checked={showDiffOnly}
                      onChange={(e) => setShowDiffOnly(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="showDiffOnly">
                      Show only changed lines
                    </label>
                  </div>
                  <small className="text-muted">
                    <span className="me-3">
                      <span style={{ backgroundColor: '#ccffd4', padding: '2px 6px', borderRadius: '3px' }}>Green</span> = Added
                    </span>
                    <span>
                      <span style={{ backgroundColor: '#ffcccc', padding: '2px 6px', borderRadius: '3px' }}>Red</span> = Removed
                    </span>
                  </small>
                </div>
                <Card>
                  <Card.Body style={{ padding: 0 }}>
                    <ReactDiffViewer
                      oldValue={JSON.stringify(JSON.parse(version1.runbookString || '{}'), null, 2)}
                      newValue={JSON.stringify(JSON.parse(version2.runbookString || '{}'), null, 2)}
                      splitView={true}
                      showDiffOnly={showDiffOnly}
                      useDarkTheme={false}
                      extraLinesSurroundingDiff={2}
                      leftTitle={`Version ${version1.versionNum} - ${version1.description || 'No description'}`}
                      rightTitle={`Version ${version2.versionNum} - ${version2.description || 'No description'}`}
                      styles={{
                        variables: {
                          light: {
                            diffViewerBackground: '#fff',
                            addedBackground: '#ccffd4',
                            addedColor: '#000',
                            removedBackground: '#ffcccc',
                            removedColor: '#000',
                            wordAddedBackground: '#4ade80',
                            wordRemovedBackground: '#f87171',
                            addedGutterBackground: '#86efac',
                            removedGutterBackground: '#fca5a5',
                            gutterBackground: '#f6f8fa',
                            gutterBackgroundDark: '#f3f4f6',
                            highlightBackground: '#fef3c7',
                            highlightGutterBackground: '#fde68a',
                          }
                        },
                        line: {
                          padding: '0.5em 0.5em',
                          fontSize: '0.85rem',
                          fontFamily: 'Consolas, Monaco, "Courier New", monospace'
                        },
                        gutter: {
                          minWidth: '50px'
                        },
                        marker: {
                          fontSize: '1rem',
                          fontWeight: 'bold'
                        }
                      }}
                    />
                  </Card.Body>
                </Card>
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          <FaTimes className="me-1" /> Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default RunbookVersionCompare;
